import * as React from "react";
import { format } from "date-fns";
import { ChevronDownIcon } from "lucide-react";
import { ButtonShadcn } from "./Button";
import { Calendar } from "./Calendar";
import { Popover, PopoverContent, PopoverTrigger } from "./Popover";
import { cn } from "@/lib/utils";

/**
 * DatePicker component built on top of react-day-picker + popover.
 *
 * @param {Date|string} [value] - Selected date value (Date object or formatted string).
 * @param {Function} [onChange] - Callback fired when date is selected. Returns formatted string.
 *
 * @param {string} [displayFormat="dd MMM yyyy"] - Format used for displaying date in UI.
 * @param {string} [outputFormat="yyyy-MM-dd"] - Format used for storing/sending to backend.
 *
 * @param {string} [placeholder="Pick a date"] - Placeholder text when no date is selected.
 * @param {string} [buttonClassName=""] - Additional class names for trigger button.
 *
 * @param {Date|string} [minDate] - Minimum selectable date.
 * @param {Date|string} [maxDate] - Maximum selectable date.
 *
 * @param {boolean} [disabled] - Disables the entire date picker.
 * @param {string} [ariaLabel] - Names the trigger for a screen reader when the visible label sits outside this component.
 *
 * @returns {JSX.Element} Rendered DatePicker component.
 */

/**
 * Parse a `yyyy-MM-dd` string (or a Date) into local midnight on that day.
 *
 * `new Date("2026-08-15")` is midnight **UTC**, while the calendar hands us
 * midnight **local**. East of Greenwich the two are a day apart, which used to
 * grey out the boundary day itself: pick From = 15 Aug and the To calendar
 * refused the 15th. Both sides of every comparison go through here.
 */
const toLocalDay = (value) => {
  if (!value) return undefined;
  if (value instanceof Date) return new Date(value.getFullYear(), value.getMonth(), value.getDate());
  const datePart = typeof value === "string" ? value.split("T")[0] : value;
  const [year, month, day] = String(datePart).split("-").map(Number);
  if (!year || !month || !day) return undefined;
  return new Date(year, month - 1, day);
};

export const DatePicker = ({
  value,
  onChange,
  displayFormat = "dd MMM yyyy",
  outputFormat = "yyyy-MM-dd",
  placeholder = "Pick a date",
  buttonClassName = "",
  calendarClassName = "",
  minDate,
  maxDate,
  disabled,
  ariaLabel,
  id,
}) => {
  // convert incoming value (string or Date) into Date object
  const parsedDate = React.useMemo(() => toLocalDay(value), [value]);
  const minDay = React.useMemo(() => toLocalDay(minDate), [minDate]);
  const maxDay = React.useMemo(() => toLocalDay(maxDate), [maxDate]);
  const [open, setOpen] = React.useState(false);

  // handle date selection from calendar
  const handleSelect = (selected) => {
    if (!selected) {
      onChange?.("");
      setOpen(false); // close
      return;
    }

    // format selected date for storage / API
    const formatted = format(selected, outputFormat);
    onChange?.(formatted);
    setOpen(false);
  };

  // format date for UI display
  const displayDate = parsedDate ? format(parsedDate, displayFormat) : null;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      {/* trigger button for opening calendar */}
      <PopoverTrigger asChild>
        <ButtonShadcn
          //   variant="outline"
          id={id}
          disabled={disabled}
          aria-label={ariaLabel}
          data-empty={!parsedDate}
          className={cn(
            // The same three as `Input`: a boundary that clears 3:1, a
            // placeholder colour from a token rather than the dark-only
            // `placeholder-zinc-500`, and a focus ring that is not the border's
            // own colour. The trigger renders its placeholder as a child
            // `<span>`, so the colour rides on `data-[empty=true]`.
            `w-full h-11
            flex items-center justify-between
            px-4 py-2.5
            rounded-lg
            border border-border-input
            bg-background-form/40
            text-text-primary
            text-sm font-normal
            transition-all duration-150
            data-[empty=true]:text-text-muted`,
            "focus:outline-none focus-visible:outline-none focus-visible:border-tone-blue focus-visible:ring-2 focus-visible:ring-tone-blue/40",
            buttonClassName,
          )}
        >
          {/* show selected date or placeholder */}
          {displayDate || <span>{placeholder}</span>}

          {/* dropdown icon */}
          <ChevronDownIcon className="h-4 w-4 opacity-50" />
        </ButtonShadcn>
      </PopoverTrigger>

      {/* calendar popup */}
      <PopoverContent
        className="min-w-[300px] p-0
        rounded-lg
        border border-border-primary
        shadow-lg"
        align="start"
      >
        <Calendar
          mode="single"
          selected={parsedDate}
          onSelect={handleSelect}
          defaultMonth={parsedDate || minDay || maxDay || new Date()}
          // disable dates outside allowed range. Both bounds are inclusive.
          disabled={(date) => (minDay && date < minDay) || (maxDay && date > maxDay)}
          className={cn("bg-background-secondary p-3 rounded-lg text-text-primary w-full", calendarClassName)}
        />
      </PopoverContent>
    </Popover>
  );
};
